import { EuroCircleFilled } from "@ant-design/icons";
import { Tooltip } from "antd";
import { Fragment, useEffect, useRef, useState } from "react";
import {
  TiArrowSortedDown,
  TiChevronLeft,
  TiChevronRight,
} from "react-icons/ti";
import { NumericFormat } from "react-number-format";
import { useQuery } from "react-query";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Area,
  AreaChart,
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  Pie,
  PieChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as Tip,
  XAxis,
  YAxis,
} from "recharts";
import { CustumedMatrix } from "tamtam-components";
import {
  getCollaboratorsSuggestions,
  getFinancialFollowUp,
  getMonthFinancial,
  getPeriodFinancialList,
  updateClientMonthlyBudget,
} from "../../api";
import { getPartners } from "../../api/partners";
import { DELETED_STATUS } from "../../config";
import _ from "../../i18n";
import {
  generateFullName,
  getTimeByDurationFormat,
  getUserNameForAvatar,
  parsePreference,
  renderTeamYears,
  toFixed,
  useOnClickOutside,
  viewDataBy,
} from "../../utils";
import SearchBox from "../common/Filter/SearchBox";
import TTPFilterHorizontalBar from "../common/Filter/TTPFilterHorizontalBar";
import { ClipBoardIcon, IconClose, SettingsIcon } from "../common/Icons";
import { billing_types, colors, tabs_1 } from "./Constants";

export default function DeltaSideBarFrame({
  organization,
  token,
  onClose,
  ...rest
}) {
  const { preferences, user, collaborator, loggedAs, language: lng } = rest;
  const [list, setList] = useState([]);
  const [totalData, setTotalData] = useState([]);
  const [totals, setTotals] = useState({
    budgetHr: 0,
    prestaTS: 0,
    economic: 0,
    turnover: 0,
  });
  const [nbResults, setNbResults] = useState(0);
  const [clients, setClients] = useState([]);
  const [pieChartData, setPieChartData] = useState([]);
  const [periodData, setPeriodData] = useState([]);
  const [followUpData, setFollowUpData] = useState(null);
  const [periodChartData, setPeriodChartData] = useState({
    task: [],
    ca: [],
    minBillingTotal: 0,
    economicBudgetToal: 0,
  });
  const [partnerOptions, setPartnerOptions] = useState([]);
  const [collaboratorOptions, setCollaboratorOptions] = useState([]);
  const [collaborators, setCollaborators] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [filter, setFilter] = useState({
    year: new Date().getFullYear(),
    // clients: [3],
    partner:
      parsePreference(preferences).financialState.allowViewDataBy === 11
        ? user?.id
        : null,
    collaborators:
      [10, 110].includes(
        parsePreference(preferences).financialState.allowViewDataBy
      ) && collaborator
        ? [collaborator.id]
        : [],
    deletedStatus: "NOT_DELETED",
    partnerZone: 1,
    page: 1,
    pageSize: 14,
    keyWord: "",
    billingType: null,
  });
  const [yearDivIsVisible, setYearDivIsVisible] = useState(false);
  const [activeView, setActiveView] = useState("TAB");
  const [updatedBudget, setUpdatedBudget] = useState({
    budgetHr: null,
    economicBudget: null,
  });
  const [isUpdating, setIsUpdating] = useState(false);

  const ref = useRef();
  useOnClickOutside(ref, () => setYearDivIsVisible(false));

  useEffect(() => {
    if (filter.partner) {
      const options = collaborators.filter((c) => c.partner == filter.partner);
      setCollaboratorOptions(options);
    } else {
      setCollaboratorOptions(collaborators);
    }
  }, [filter.partner, collaborators]);

  useEffect(() => {
    const selected =
      list && list.filter((client) => clients.includes(client.clientId));

    const newTotals =
      selected && selected.length > 0
        ? selected.reduce(
            (acc, client) => {
              acc.budgetHr += parseFloat(client.budgetHr.total) || 0;
              acc.prestaTS += client.prestaTS.total || 0;
              acc.economic += parseFloat(client.economic.total) || 0;
              acc.turnover += parseFloat(client.turnover.total) || 0;
              return acc;
            },
            { budgetHr: 0, prestaTS: 0, economic: 0, turnover: 0 }
          )
        : { ...totalData };

    setTotals(newTotals);
  }, [list, clients, totalData]);

  useEffect(() => {
    if (!list) return;
    const selected =
      clients &&
      clients.length > 0 &&
      list.filter((client) => clients.includes(client.clientId));

    let chartData;
    let minBillingTotal = 0;
    if (selected.length > 0) {
      const taskByCategory = {};
      selected.forEach((client) => {
        const tasks = client.task?.sub || {};

        for (const key in tasks) {
          const task = tasks[key];
          if (!taskByCategory[key]) {
            taskByCategory[key] = {
              label: task.label,
              duration: 0,
              valuation: 0,
            };
          }

          minBillingTotal += parseFloat(task.valuation) || 0;
          taskByCategory[key].duration += task.duration || 0;
          taskByCategory[key].valuation += parseFloat(task.valuation) || 0;
        }
      });

      chartData = Object.values(taskByCategory).map((task, index) => ({
        name: task.label,
        value: task.valuation,
        fill: colors[index % colors.length],
      }));
    } else {
      chartData =
        totalData &&
        totalData.task &&
        Object.values(totalData.task.sub).map((task, index) => ({
          name: task.label,
          value: task.valuation,
          fill: colors[index % colors.length],
        }));
    }

    setPeriodChartData({
      ...periodChartData,
      minBillingTotal,
    });
    setPieChartData(chartData);
  }, [list, clients, totalData]);

  let financialSheetListQuery = useQuery(
    ["financialSheetList", token, organization.id, filter],
    async () => {
      if (token && organization) {
        try {
          const response = await getPeriodFinancialList(
            token,
            organization.id,
            { ...filter }
          );
          return response.data.data;
        } catch (error) {
          console.error("Financial List >> Err: ", error);
          return [];
        }
      }
    },
    {
      enabled: !!token && !!organization && !!filter,
      onSuccess: (data) => {
        setClients([data.list.pagination[0].clientId]);
        setNbResults(data.list.totalCount);
        setTotalData(data.total);
        setList(data.list.pagination);
      },
    }
  );

  let financialPeriodDataQuery = useQuery(
    ["financialPriodData", token, organization.id, clients, filter],
    async () => {
      if (token && organization) {
        try {
          const response = await getMonthFinancial(token, organization.id, {
            ...filter,
            clients,
          });
          return response.data.data;
        } catch (error) {
          console.error("Financial List >> Err: ", error);
          return [];
        }
      }
    },
    {
      enabled: !!token && !!organization && !!clients && !!filter,
      onSuccess: (data) => {
        setPeriodData(data);
      },
    }
  );

  const handleSave = (updatedBudget) => {
    const hourBudget = periodData
      .filter((elem) => elem.month !== "")
      .reduce((acc, elem) => {
        acc[elem.month] = parseFloat(elem.budget.budgetHr).toFixed(2);
        return acc;
      }, {});

    const economicBudget = periodData
      .filter((elem) => elem.month !== "")
      .reduce((acc, elem) => {
        acc[elem.month] = parseFloat(elem.budget.economicBudget).toFixed(2);
        return acc;
      }, {});
    const payload = {
      clientId: clients[0],
      year: filter.year,
      hourBudget: updatedBudget?.budgetHr
        ? updatedBudget?.budgetHr
        : hourBudget,
      economicBudget: updatedBudget?.economicBudget
        ? updatedBudget?.economicBudget
        : economicBudget,
    };

    updateClientMonthlyBudget(token, organization.id, payload)
      .then((resp) => {
        if (resp.data.result === "OK") {
          toast.success(_("successfully_added"));
          followUpPeriodDataQuery.refetch();
        }
      })
      .catch((e) => {
        if (
          e.response &&
          e.response.data &&
          e.response.data.errors &&
          e.response.data.errors.length > 0
        )
          toast.error(_(e.response.data.errors[0].message));
        else toast.error(_("error"));
      })
      .finally(() => setIsUpdating(false));
  };

  let followUpPeriodDataQuery = useQuery(
    ["followPriodData", token, organization.id, clients, filter],
    async () => {
      if (financialSheetListQuery.isFetched && token && organization) {
        try {
          const response = await getFinancialFollowUp(token, organization.id, {
            ...filter,
            clients,
          });
          return response.data.data;
        } catch (error) {
          console.error("Financial List >> Err: ", error);
          return [];
        }
      }
    },
    {
      enabled: !!token && !!organization && !!clients && !!filter,
      onSuccess: (data) => {
        setFollowUpData(data);

        const taskData = [];
        const caData = [];

        if (data) {
          const filteredData = data.filter((item) => item.month !== "");
          let accumulatedTurnover = 0;

          filteredData.forEach((item) => {
            const month = item.month;
            const budget = item.followUp;

            if (budget) {
              taskData.push({
                month,
                budgetHr: budget.budgetHr / 60,
                prestaTs: budget.prestaTs / 60,
              });

              accumulatedTurnover += budget.turnover;

              caData.push({
                month,
                turnover: budget.turnover,
                economicBudget: budget.economicBudget,
                accumulateTurnover: accumulatedTurnover,
              });
            }
          });
        }

        setPeriodChartData({
          ...periodChartData,
          task: taskData,
          ca: caData,
        });
      },
    }
  );

  let partnersQuery = useQuery(
    ["partnersQuery", token, organization.id],
    async () => {
      if (token && organization) {
        const response = await getPartners({
          token,
          organizationId: organization?.id,
        });
        const data = response.data.data.map((partner) => ({
          value: partner.id,
          label: partner.name,
          zone: partner.zone,
        }));
        setPartnerOptions(data);
        if (parsePreference(preferences.clients).allowViewDataBy === 11) {
          const zone = data.find((p) => p.value === Number(user?.id)).zone;
          setFilter({ ...filter, partnerZone: zone });
        }
        return data;
      }
    },
    {
      enabled: !!token && !!organization,
    }
  );

  let collaboratorsdQuery = useQuery(
    ["getCollaborators", token, organization.id],
    async () => {
      if (token && organization) {
        try {
          const response = await getCollaboratorsSuggestions(
            token,
            organization.id
          );
          let data = response.data.data;
          let list = viewDataBy(
            preferences,
            data,
            collaborator,
            "dynamicTable"
          );
          data = list
            .filter((item) => item !== null)
            .map((t) => {
              return {
                value: t.id,
                label: t.name,
                partner: t?.partner?.value,
                isActif: t.isActif,
              };
            });
          setCollaborators(
            list.map((c) => ({
              value: c.id,
              label: c.name,
              partner: c?.partner?.value,
              isActif: c.isActif,
            }))
          );
          setCollaboratorOptions(data);
        } catch (e) {
          return null;
        }
      }
    }
  );

  const handleColumns = (column, line) => {
    const currentMonth = new Date().toLocaleString("en-US", { month: "long" });
    const currentIndex = new Date(`${currentMonth} 1, 2000`).getMonth();
    switch (column.id) {
      case "budget":
        return (
          <div
            style={{
              display: "flex",
              justifyContent: "space-around",
              alignItems: "center",
              gap: 10,
              fontSize: 12,
            }}
          >
            {Object.keys(line.budget).map((key, index) => (
              <div
                key={index}
                style={{
                  flex: 1,
                  textAlign: "center",
                  // textDecoration: (() => {
                  //   if (!followUpData) return "none";

                  //   const matchedItem = followUpData.find(
                  //     (item) => item.month == line.month
                  //   );
                  //   if (!matchedItem || !matchedItem.followUp) return "none";

                  //   const followUpValue = Math.ceil(matchedItem.followUp[key]);
                  //   const budgetValue = Math.ceil(line.budget[key]);

                  //   return followUpValue > budgetValue ||
                  //     followUpValue < budgetValue
                  //     ? "1px #de4848 line-through"
                  //     : "none";
                  // })(),
                }}
              >
                {periodData.indexOf(line) === 0 && <span>{_(key)}</span>}
                {periodData.indexOf(line) > 0 ? (
                  key == "budgetHr" ? (
                    getTimeByDurationFormat(toFixed(line.budget[key] / 60, 2))
                  ) : (
                    <NumericFormat
                      value={line.budget[key].toFixed(2)}
                      displayType={"text"}
                      thousandSeparator={"."}
                      decimalSeparator={","}
                      prefix={"€ "}
                    />
                  )
                ) : (
                  <></>
                )}
              </div>
            ))}
          </div>
        );
      case "followUp":
        return (
          <div
            style={{
              display: "flex",
              justifyContent: "space-around",
              alignItems: "center",
              gap: 10,
              fontSize: 12,
            }}
          >
            {Object.keys(line.followUp).map((key, index) => {
              const isFirstLine =
                followUpData && followUpData.indexOf(line) === 0;
              const isNotFirstLine =
                followUpData && followUpData.indexOf(line) > 0;
              const isBudgetOrPresta = key === "budgetHr" || key === "prestaTs";
              const isPresta = key === "prestaTs";
              const isTurnover = key === "turnover";
              const isEconomicBudget = key === "economicBudget";
              const isBudgetEditable =
                key === "budgetHr" || key === "economicBudget";

              const monthIndex = new Date(`${line.month} 1, 2000`).getMonth();
              const isCurrentYear = filter.year === new Date().getFullYear();
              const showValue = isCurrentYear
                ? monthIndex <= currentIndex
                : true;
              const isEditable = isCurrentYear && monthIndex >= currentIndex;

              const renderValue = () => {
                if (isBudgetEditable && isEditable) {
                  const defaultValue = isEconomicBudget
                    ? line.followUp[key].toFixed(2)
                    : (line.followUp[key] / 60).toFixed(2);
                  return (
                    <div
                      key={index}
                      className="input-field"
                      style={{
                        opacity:
                          clients.length > 1 || clients.length == 0 ? 0.7 : 1,
                      }}
                    >
                      <input
                        type="number"
                        disabled={clients.length > 1 || clients.length == 0}
                        defaultValue={defaultValue}
                        onChange={(e) =>
                          handleInputChange(
                            key,
                            line.month,
                            Number(defaultValue),
                            Number(e.target.value)
                          )
                        }
                        style={{
                          cursor:
                            clients.length > 1 ||
                            (clients.length == 0 && "not-allowed"),
                        }}
                      />
                      {isEconomicBudget ? <span>€</span> : <span>h</span>}
                    </div>
                  );
                }

                if (isBudgetOrPresta) {
                  if (isPresta && !showValue) return <div />;
                  return getTimeByDurationFormat(
                    toFixed(line.followUp[key] / 60, 2)
                  );
                }

                const formattedValue = (
                  <NumericFormat
                    value={line.followUp[key].toFixed(2)}
                    displayType="text"
                    thousandSeparator="."
                    decimalSeparator=","
                    prefix="€ "
                  />
                );

                if (isTurnover && !showValue) return <div />;
                return formattedValue;
              };

              return (
                <div key={index} style={{ flex: 1, textAlign: "center" }}>
                  {isFirstLine && <span>{_(key)}</span>}
                  {isNotFirstLine ? renderValue() : null}
                </div>
              );
            })}
          </div>
        );
    }
  };

  const handleTitle = (title) => (
    <Fragment>
      {periodData.indexOf(title) > 0 && (
        <div
          style={{ border: 0, display: "flex", justifyContent: "flex-start" }}
        >
          <div style={{ border: 0, textAlign: "left" }}>
            {_(title.month).substring(0, 3)}
          </div>
        </div>
      )}
    </Fragment>
  );

  const renderActiveViewContent = () => {
    switch (activeView) {
      case "TAB":
        return (
          <div className="follow-up-container">
            <div>
              {periodData && (
                <CustumedMatrix
                  columns={[
                    {
                      id: "budget",
                      label: `${_("budgetObjectif")} ${filter.year}`,
                      isEditable: true,
                      widthPercentage: 75,
                    },
                  ]}
                  items={periodData}
                  handleCellule={handleColumns}
                  handleTitle={handleTitle}
                  headerTagBG={"#FFFFFF"}
                  headerTagColor
                />
              )}
            </div>
            <div className="follow-up-matrix">
              {followUpData && followUpData.length > 0 ? (
                <CustumedMatrix
                  columns={[
                    {
                      id: "followUp",
                      label: _("Follow-Up"),
                      isEditable: true,
                      widthPercentage: 100,
                    },
                  ]}
                  items={followUpData}
                  handleCellule={handleColumns}
                  handleTitle={(title) => <Fragment></Fragment>}
                  headerTagBG={"#FFFFFF"}
                  headerTagColor
                />
              ) : (
                <div className="follow-up-generate-btn">
                  <div>
                    <img
                      src="https://stats.tamtam.pro/img/icons/manage-BM/follow-up.svg"
                      alt="follow-up-img"
                    />
                  </div>
                  <h4>Aucun Follow-up n'a été généré</h4>
                  <p>
                    {` Prenez le temps de configurer soigneusement votre budget
                    objectif ${filter.year} avant de générer tout follow-up.`}
                  </p>
                  <button
                    disabled={clients.length > 1 || clients.length == 0}
                    style={{
                      cursor:
                        clients.length > 1 || clients.length == 0
                          ? "not-allowed"
                          : "pointer",
                      opacity:
                        clients.length > 1 || clients.length == 0 ? 0.7 : 1,
                    }}
                    onClick={handleSave}
                  >
                    Générer Follow-Up
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      case "CHART":
        return (
          <div
            style={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: 50,
            }}
          >
            <div className="chart">
              <h3>Réalisé Horaire par Mois</h3>
              <ResponsiveContainer>
                <AreaChart
                  width={300}
                  height={100}
                  data={periodChartData.task}
                  margin={{
                    top: 10,
                    right: 30,
                    left: 0,
                    bottom: 0,
                  }}
                >
                  <defs>
                    <linearGradient id="colorTask" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#01ae8e" stopOpacity={1} />
                      <stop offset="95%" stopColor="#01ae8e" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <defs>
                    <linearGradient id="colorHr" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#18a0fb" stopOpacity={1} />
                      <stop offset="95%" stopColor="#18a0fb" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 0"
                    vertical={false}
                    stroke="#64748B10"
                  />
                  <XAxis
                    dataKey="month"
                    tickFormatter={(month) => {
                      return _(month).substring(0, 3);
                    }}
                  />
                  <YAxis
                    axisLine={false}
                    type="number"
                    tickLine={false}
                    tickSize={10}
                    unit="h"
                    tickMargin={20}
                    fill="#64748B"
                  />
                  <Tip
                    content={<CustomTooltip />}
                    cursor={{ fill: "rgba(225, 228, 232, 0.50)" }}
                  />
                  <Area
                    type="monotone"
                    dataKey="budgetHr"
                    stroke="#18a0fb"
                    fill="url(#colorHr)"
                  />
                  <Area
                    type="monotone"
                    dataKey="prestaTs"
                    stroke="#01ae8e"
                    fill="url(#colorTask)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="chart" style={{ marginTop: 50 }}>
              <h3>Chiffre d'Affaires par Mois</h3>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                  width={500}
                  height={300}
                  data={periodChartData.ca}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <defs>
                    <linearGradient
                      id="colorEconomic"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="0" stopColor="#18a0fb" stopOpacity={1} />
                      <stop offset="100%" stopColor="#18a0fb" stopOpacity={0} />
                    </linearGradient>
                  </defs>

                  <defs>
                    <linearGradient id="colorCA" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0" stopColor="#d87317" stopOpacity={1} />
                      <stop offset="100%" stopColor="#d87317" stopOpacity={0} />
                    </linearGradient>
                  </defs>

                  <CartesianGrid
                    strokeDasharray="3 0"
                    vertical={false}
                    stroke="#64748B10"
                  />
                  <XAxis
                    dataKey="month"
                    tickFormatter={(month) => _(month).substring(0, 3)}
                  />
                  <YAxis
                    axisLine={false}
                    type="number"
                    tickLine={false}
                    tickSize={10}
                    unit="€"
                    tickMargin={20}
                    fill="#64748B"
                    domain={[
                      0,
                      (dataMax) =>
                        Math.max(
                          dataMax,
                          Math.max(
                            Math.round(periodChartData.minBillingTotal),
                            Math.round(totals?.economic)
                          ) + 300
                        ),
                    ]}
                  />
                  <ReferenceLine
                    y={periodChartData.minBillingTotal}
                    stroke="#de4848"
                    strokeDasharray="0"
                    label={{
                      value: `${_(
                        "minimumBilling"
                      )} ${periodChartData.minBillingTotal.toFixed(2)}€`,
                      position: "insideBottomRight",
                      fill: "#de4848",
                      fontSize: 12,
                    }}
                  />
                  <ReferenceLine
                    y={totals?.economic}
                    stroke="#18a0fb"
                    strokeDasharray="0"
                    label={{
                      value: `${_("economicBudget")} ${totals?.economic}€`,
                      position: "insideTopRight",
                      fill: "#18a0fb",
                      fontSize: 12,
                    }}
                  />
                  <Tip
                    content={<CustomTooltip />}
                    cursor={{ fill: "rgba(225, 228, 232, 0.50)" }}
                  />

                  <Bar
                    dataKey="economicBudget"
                    fill="url(#colorEconomic)"
                    stroke="#18a0fb75"
                    barSize={15}
                    radius={[1, 1, 0, 0]}
                  />

                  <Bar
                    dataKey="turnover"
                    fill="url(#colorCA)"
                    stroke="#d8731775"
                    barSize={15}
                    radius={[1, 1, 0, 0]}
                  />

                  <Line
                    type="monotone"
                    dataKey="accumulateTurnover"
                    stroke="#6c7e92"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    activeDot={{ r: 5 }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>
        );
      default:
        return <div />;
    }
  };

  const renderYearSelect = () => {
    return (
      <div
        ref={ref}
        className={
          "customed_select" +
          " " +
          (yearDivIsVisible ? "customed_select_opened" : "")
        }
        style={{
          background: "#6d7f92",
          color: "#ffffff",
          zIndex: 9999,
        }}
      >
        <div
          className={"customed_select_header"}
          onClick={() => setYearDivIsVisible(!yearDivIsVisible)}
        >
          <span>{filter.year}</span>
          <div className={"actions"}>
            <div>
              <span className={"action"}>
                <TiArrowSortedDown />
              </span>
            </div>
          </div>
        </div>
        {yearDivIsVisible && (
          <div className={"customed_select_body"}>
            <div className={"customed_select_body_options"}>
              {renderTeamYears().map((item, index) => (
                <div
                  key={index}
                  className={"customed_select_body_option"}
                  onClick={() => setFilter({ ...filter, year: item })}
                  style={{ zIndex: 4 }}
                >
                  {item}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const handleInputChange = (column, month, prevValue, value) => {
    setIsUpdating(true);

    const key = column === "budgetHr" ? "budgetHr" : "economic";
    setTotals((prevTotals) => ({
      ...prevTotals,
      [key]: prevTotals[key] - prevValue + value,
    }));

    const newFollowUpData = followUpData.map((item) =>
      item.month == month
        ? {
            ...item,
            followUp: {
              ...item.followUp,
              [column]: key == "budgetHr" ? value * 60 : value,
            },
          }
        : { ...item }
    );

    setFollowUpData(newFollowUpData);

    setUpdatedBudget((prev) => ({
      ...prev,
      [column]: {
        ...(prev[column] || {}),
        [month]: column === "budgetHr" ? value * 60 : value,
      },
    }));
  };

  const handleOnCancel = () => {
    setIsUpdating(false);
    setUpdatedBudget({
      hour: null,
      economic: null,
    });
  };

  const handlePartnerChange = (partner) => {
    const selected = partnerOptions.find((p) => p.value === Number(partner));
    setSelectedPartner(selected);
    setFilter({
      ...filter,
      partner: Number(partner),
      partnerZone: selected?.zone,
    });
  };

  const handleSetDeletedStatus = (status) => {
    setFilter({ ...filter, deletedStatus: status });
  };

  const handleCollaboratorsChange = (items) => {
    const actifs = collaborators
      .filter((item) => item.isActif)
      .map((item) => item.value);

    const updatedSelection = ["SUPERADMIN", "ADMIN"].includes(loggedAs)
      ? [...items]
      : [...actifs, ...items];

    setFilter({ ...filter, collaborators: [...new Set(updatedSelection)] });
  };

  const filters = [
    {
      type: "SELECT",
      options: {
        isSimple: false,
        isMulti: true,
        isClearable: false,
        isSearch: true,
        placeholder: _("collaborator"),
        options: collaboratorOptions,
        values: filter.collaborators,
        action: handleCollaboratorsChange,
      },
    },
    {
      type: "SELECT",
      options: {
        isSimple: true,
        isClearable: false,
        placeholder: _("clientState"),
        options: DELETED_STATUS,
        values: filter.deletedStatus,
        action: handleSetDeletedStatus,
      },
    },
  ];

  if (
    [1, 11].includes(
      parsePreference(preferences).financialState.allowViewDataBy
    )
  ) {
    filters.unshift({
      type: "SELECT",
      options: {
        isSimple: true,
        isClearable: true,
        isSearch: true,
        isDisabled:
          parsePreference(preferences).financialState.allowViewDataBy === 11,
        placeholder: _("partner"),
        options: partnerOptions,
        values:
          loggedAs === "COLLABORATOR" &&
          parsePreference(preferences).financialState.allowViewDataBy === 11
            ? collaborator?.partner?.value
            : filter.partner,
        action: handlePartnerChange,
      },
    });
  }

  const renderFilterFrame = () => {
    const label = selectedPartner?.label || "";
    const [firstName, lastName] = label.split(" ");

    return (
      <div>
        <div
          className="principal-line"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div className="billing_types_wrapper">
            {selectedPartner && (
              <Fragment>
                <Tooltip placement="top" title={selectedPartner?.label}>
                  {renderAvatar("", firstName, lastName)}
                </Tooltip>
                <div className="divider" />
              </Fragment>
            )}
            <div className="billing_types">
              {billing_types.map((t, index) => (
                <div
                  key={index}
                  style={{ "--border-color": t.color }}
                  className={`${
                    filter.billingType == t.value ? "active_billing_type" : ""
                  }`}
                  onClick={() => setFilter({ ...filter, billingType: t.value })}
                >
                  {t.label}
                </div>
              ))}
            </div>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
            }}
          >
            {renderYearSelect()}
            <TTPFilterHorizontalBar
              button
              bg="#FFF"
              subBG="F8F9FA"
              filter={filter}
              filters={filters}
              nbResult={nbResults}
              // pageSize={pageSize}
              updateFilterContent={setFilter}
              cssClass="filter-horizontal-button-delta"
            />
          </div>
        </div>
      </div>
    );
  };

  const { pageSize, page } = filter;

  const CustomLegend = () => (
    <div
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 7,
      }}
    >
      {pieChartData.map((entry, index) => (
        <div
          key={`legend-${index}`}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 7,
            width: "100%",
          }}
        >
          <div
            style={{
              backgroundColor: colors[index % colors.length],
              width: 10,
              height: 10,
              borderRadius: "100%",
            }}
          />
          <div
            className="legend-content"
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 10,
              width: "100%",
            }}
          >
            {entry.name && entry.name.length > 40 ? (
              <Tooltip placement="top" title={entry.name} mouseLeaveDelay={0}>
                <span className="legend-name">{`${entry.name.substring(
                  0,
                  40
                )}...`}</span>
              </Tooltip>
            ) : (
              <span className="legend-name">{entry.name}</span>
            )}
            <NumericFormat
              value={entry.value.toFixed(1)}
              displayType={"text"}
              thousandSeparator={"."}
              decimalSeparator={","}
              prefix="€ "
            />
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <Fragment>
      <div
        style={{
          width: "100%",
          height: "100%",
        }}
      >
        <div className="sidebar_header">
          <div className="sidebar_header_infos">
            <h3>{_("turnoverManagement")}</h3>
          </div>
          <div className="sidebar_header_right">
            {isUpdating && (
              <div>
                <button
                  className="cancel-btn"
                  onClick={handleOnCancel}
                  style={{ "--bg-color": "#f1f2f4", "--color": "#29394d" }}
                >
                  {_("cancel")}
                </button>
                <button
                  className="save-btn"
                  onClick={() => handleSave(updatedBudget)}
                >
                  {_("save")}
                </button>
              </div>
            )}
            <div className="sidebar_header_action" onClick={onClose}>
              <IconClose size={12} />
            </div>
          </div>
        </div>
        <div className="sidebar_body vertical_content">
          {renderFilterFrame()}
          <div className="content">
            <div className="financial_delta_list">
              <SearchBox
                placeholder={`${_("search")}...`}
                onChange={(item) => {
                  setFilter({ ...filter, keyWord: item });
                }}
                filter={filter}
              />
              {financialSheetListQuery.isFetching ? (
                <div style={{ padding: "100px 0" }}>Loading...</div>
              ) : list ? (
                list.map((item, index) => {
                  return (
                    <div
                      key={`${index}-${item.clientId}`}
                      className={`list_item ${
                        clients.includes(item.clientId) ? "selected" : ""
                      }`}
                      onClick={() =>
                        // setClients((prev) => {
                        //   const isSelected = prev.includes(item.clientId);

                        //   if (isSelected) {
                        //     if (prev.length === 1) return prev;
                        //     return prev.filter((id) => id !== item.clientId);
                        //   } else {
                        //     return [...prev, item.clientId];
                        //   }
                        // })

                        setClients((prev) => {
                          const isSelected = prev.includes(item.clientId);

                          if (isSelected) {
                            // if (prev.length === 1) return prev;
                            return prev.filter((id) => id !== item.clientId);
                          } else {
                            return [...prev, item.clientId];
                          }
                        })
                      }
                    >
                      {item.clientName && item.clientName.length > 25 ? (
                        <Tooltip
                          placement="top"
                          title={item.clientName}
                          mouseLeaveDelay={0}
                        >
                          <span>{`${item.clientName.substring(
                            0,
                            25
                          )}...`}</span>
                        </Tooltip>
                      ) : (
                        <span>{item.clientName}</span>
                      )}

                      <div className="underlay">
                        <Tooltip
                          key="tool-2"
                          placement="top"
                          title={_("clientForm")}
                          mouseLeaveDelay={0}
                        >
                          <Link to={`/${lng}/fiche-client/${item.clientId}`}>
                            <ClipBoardIcon />
                          </Link>
                        </Tooltip>
                        <Tooltip
                          key="tool-1"
                          placement="top"
                          title={_("manageBudget")}
                          mouseLeaveDelay={0}
                        >
                          <Link
                            to={`/${lng}/budget/${item.clientId}/${filter.year}`}
                          >
                            <SettingsIcon />
                          </Link>
                        </Tooltip>
                        {item.portal ? (
                          <Link to={item.portal} target="_blank">
                            <Tooltip
                              placement="top"
                              title={
                                <div
                                  style={{
                                    textAlign: "left",
                                  }}
                                >
                                  Portail client
                                </div>
                              }
                            >
                              <img
                                src="https://static.intercomassets.com/avatars/6472131/square_128/Group_39910-1744282782.png"
                                alt="recovr_logo"
                                width={15}
                              />
                            </Tooltip>
                          </Link>
                        ) : (
                          ""
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div style={{ padding: "100px 0" }}>{_("noData")}</div>
              )}
              <div
                className="paginator"
                style={{ display: "flex", gap: "8px", marginTop: "10px" }}
              >
                <button
                  disabled={page <= 1}
                  onClick={() =>
                    setFilter((prev) => ({
                      ...prev,
                      page: prev.page - 1,
                    }))
                  }
                >
                  <TiChevronLeft />
                </button>
                <p>{`Page ${page}/${Math.ceil(nbResults / pageSize)}`}</p>
                <button
                  disabled={list && list.length < pageSize}
                  onClick={() =>
                    setFilter((prev) => ({
                      ...prev,
                      page: prev.page + 1,
                    }))
                  }
                >
                  <TiChevronRight />
                </button>
              </div>
            </div>
            <div className="graph-container">
              <div className="tabs">
                {tabs_1.map((tab) => (
                  <div
                    key={tab.id}
                    className={`tab ${
                      activeView == tab.id ? "active-tab" : ""
                    }`}
                    onClick={() => setActiveView(tab.id)}
                  >
                    {tab.icon}
                    {tab.label}
                  </div>
                ))}
              </div>
              {financialSheetListQuery.isFetching ||
              financialPeriodDataQuery.isFetching ? (
                <div className="ripple_loading">
                  <img src={"/img/Ripple-1s-150px.svg"} alt="ripple" />
                </div>
              ) : financialSheetListQuery.isFetched &&
                financialPeriodDataQuery.isFetched ? (
                <div className="view-content">{renderActiveViewContent()}</div>
              ) : (
                <div>{_("noData")}</div>
              )}
            </div>
            <div className="circular-chart-container">
              {totals ? (
                <div className="sub-container">
                  <div id="budgetHr" className="circular-chart">
                    <div>
                      <p>{`${_("budgetHr")} Annuel`}</p>
                      <strong>
                        {getTimeByDurationFormat(totals?.budgetHr || 0)}
                      </strong>
                    </div>
                    <CircularChart
                      id="budgetHr"
                      label={_("prestaTs")}
                      total={totals?.budgetHr || 0}
                      value={totals?.prestaTS || 0}
                      color="#02af8e"
                    />
                  </div>
                  <div id="economic" className="circular-chart">
                    <div>
                      <p>{`${_("economicBudget")} Annuel`}</p>
                      <NumericFormat
                        value={totals?.economic?.toFixed(2) || 0}
                        displayType={"text"}
                        thousandSeparator={"."}
                        decimalSeparator={","}
                        prefix="€ "
                      />
                    </div>
                    <CircularChart
                      id="economic"
                      label={_("turnover")}
                      type="*"
                      total={totals?.economic || 0}
                      value={totals?.turnover || 0}
                      color="#d87318"
                    />
                  </div>
                </div>
              ) : (
                ""
              )}
              <div className="pie-chart-wrapper">
                {pieChartData && pieChartData.length > 0 ? (
                  <Fragment>
                    <div>
                      <p>{_("minimumBilling")}</p>
                      <NumericFormat
                        value={pieChartData
                          .reduce((sum, entry) => sum + entry.value, 0)
                          .toFixed(2)}
                        displayType={"text"}
                        thousandSeparator={"."}
                        decimalSeparator={","}
                        prefix="€ "
                      />
                    </div>
                    <Tooltip
                      placement="leftBottom"
                      className="legend-tooltip"
                      overlayInnerStyle={{
                        color: "#29394d",
                        width: 400,
                        position: "absolute",
                        right: 0,
                        bottom: 0,
                        fontSize: 12,
                        fontWeight: 500,
                      }}
                      color="#f0f0f0"
                      title={<CustomLegend />}
                    >
                      <div className="euro-sign">
                        <EuroCircleFilled />
                      </div>
                    </Tooltip>
                    <div className="chart-container">
                      <div className="chart-area">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={pieChartData}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              outerRadius={90}
                              fill="#8884d8"
                              dataKey="value"
                            >
                              {pieChartData.map((entry, index) => (
                                <Cell
                                  key={`cell-${index}`}
                                  fill={colors[index % colors.length]}
                                />
                              ))}
                            </Pie>
                            <Tooltip
                              formatter={(value) => `$${value.toFixed(2)}`}
                            />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </Fragment>
                ) : (
                  <div className="empty-state">
                    <p>No task data available</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
}

const CircularChart = ({
  label,
  total,
  value,
  color,
  id = "progress",
  type = "time",
}) => {
  const progress = total > 0 ? (value / total) * 100 : 0;
  const gradientId = `textGradient-${id}`;

  return (
    <Fragment>
      <svg
        width="120"
        height="120"
        viewBox="0 0 120 120"
        className="circular-progress"
        style={{
          "--progress": progress,
        }}
      >
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop
              offset="0%"
              style={{ stopColor: "#29394d", stopOpacity: 1 }}
            />
            <stop offset="100%" style={{ stopColor: color, stopOpacity: 1 }} />
          </linearGradient>
        </defs>
        <circle className="bg"></circle>
        <circle className="fg" style={{ "--fill": color }}></circle>
        <text
          x="50%"
          y="50%"
          textAnchor="middle"
          dy="0.3em"
          className="progress-text"
          fill={`url(#${gradientId})`}
        >
          {`${Math.round(progress)}%`}
        </text>
      </svg>
      <div className="legend">
        <div className="legend-color" style={{ backgroundColor: color }} />
        <div className="legend-text">
          <p>{label}</p>
          <strong>
            {type == "time" ? (
              getTimeByDurationFormat(value)
            ) : (
              <NumericFormat
                value={value ? value.toFixed(2) : 0}
                displayType={"text"}
                thousandSeparator={"."}
                decimalSeparator={","}
                prefix="€ "
              />
            )}
          </strong>
        </div>
      </div>
    </Fragment>
  );
};

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload || payload.length === 0) return null;

  const { month } = payload[0].payload;

  return (
    <div className="custom_tooltip">
      <div className="custom_tooltip_child">{_(month)}</div>
      {payload.map((item, index) => (
        <div key={`${index}-${item.month}`} className="custom_tooltip_child">
          <div>
            <div className="square" style={{ backgroundColor: item.stroke }} />
            <span>{_(item.name)}</span>
          </div>
          <div>
            {item.name == "prestaTs" || item.name == "budgetHr" ? (
              getTimeByDurationFormat(item.value)
            ) : (
              <NumericFormat
                value={item.value.toFixed(2)}
                displayType={"text"}
                thousandSeparator={"."}
                decimalSeparator={","}
                prefix={
                  item.name == "turnover" || item.name == "economicBudget"
                    ? "€ "
                    : ""
                }
                suffix={item.name == "effectifRate" ? " €/h" : ""}
              />
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

const renderAvatar = (avatarUrl, firstName, lastName) => {
  if (!avatarUrl && !firstName && !lastName) {
    return (
      <div className="avatar_not_exist" style={{ cursor: "pointer" }}>
        <p style={{ margin: "auto" }}>-</p>
      </div>
    );
  }
  let avatarDiv;
  if (!avatarUrl) {
    avatarDiv = (
      <div className="avatar_not_exist" style={{ cursor: "pointer" }}>
        <span>{getUserNameForAvatar(firstName, lastName)}</span>
      </div>
    );
  } else {
    avatarDiv = (
      <div className="avatar_exist" style={{ cursor: "pointer" }}>
        <img src={avatarUrl} alt={generateFullName(firstName, lastName)} />
      </div>
    );
  }

  return avatarDiv;
};
